import React, { createContext, useContext, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { db, functions } from '../../../firebaseConfig';
import { collection, query, where, orderBy, limit, doc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  setActiveConversationId as setActiveConversationIdAction,
  setConversations,
  updateConversation,
} from '../../../redux/slices/chatSlice';


/**
 * Conversation Context
 * Handles conversation list, active conversation, and CRUD operations
 */
const ConversationContext = createContext(null);

export { ConversationContext };

export const useConversation = () => {
  const ctx = useContext(ConversationContext);
  if (!ctx) {
    throw new Error('useConversation must be used within ConversationProvider');
  }
  return ctx;
};

const CHAT_DEV_FALLBACK_ENABLED =
  process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_CHAT_DEV_FALLBACK === 'true';

export const ConversationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  // Redux Selectors
  const currentUser = useSelector(state => state.auth.currentUser);
  const conversations = useSelector(state => state.chat.conversations);
  const activeConversationId = useSelector(state => state.chat.activeConversationId);
  const membersByConversation = useSelector(state => state.chat.membersByConversation);

  // Computed
  const conversationMap = useMemo(
    () => Object.fromEntries((conversations || []).map(c => [c.id, c])),
    [conversations]
  );

  // Actions
  const setActiveConversationId = useCallback((id) => {
    dispatch(setActiveConversationIdAction(id));
  }, [dispatch]);

  // FIX 1.3: Track when URL was set to avoid stale URL overriding user clicks
  const lastUrlCidRef = useRef(null);

  // FIX 4.3: Ref for fresh activeConversationId in async callbacks
  const activeConvIdRef = useRef(activeConversationId);
  activeConvIdRef.current = activeConversationId;

  // URL sync - reflect URL changes into activeConversationId
  // FIX 1.3: Only sync when location.search actually changes, not on activeConversationId change
  useEffect(() => {
    try {
      const params = new URLSearchParams(location?.search || '');
      const cid = params.get('conversationId');
      if (cid && cid !== lastUrlCidRef.current) {
        lastUrlCidRef.current = cid;
        setActiveConversationId(cid);
      }
    } catch (e) {
      console.warn('[Conversation] Failed to sync activeConversationId from URL', e);
    }
  }, [location.search, setActiveConversationId]);

  // Subscribe to conversations for current user
  useEffect(() => {
    if (!currentUser?.uid) {
      dispatch(setConversations([]));
      return undefined;
    }

    const q = query(
      collection(db, 'conversations'),
      where('memberIds', 'array-contains', currentUser.uid),
      orderBy('lastMessageAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dispatch(setConversations(items));
    }, (error) => {
      console.error('[Conversation] listener error', error);
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser?.uid, dispatch]);

  // Open direct conversation with user
  const openDirectConversationWithUser = useCallback(async (targetUserId) => {
    if (!currentUser?.uid || !targetUserId || targetUserId === currentUser.uid) return null;

    const [u1, u2] = [currentUser.uid, targetUserId].sort();
    const fallbackConvId = `direct_${u1}_${u2}`;

    // OPTIMIZATION: Check local cache first to avoid 1s+ Cloud Function delay
    const existingConv = conversations?.find(c => 
      c.type === 'direct' && 
      c.memberIds?.includes(u1) && 
      c.memberIds?.includes(u2) &&
      c.memberIds?.length === 2
    );

    if (existingConv) {
      setActiveConversationId(existingConv.id);
      return existingConv.id;
    }

    try {
      const ensureConv = httpsCallable(functions, 'ensureDirectConversation');
      const res = await ensureConv({ targetUserId });
      let conversationId = res?.data?.conversationId || fallbackConvId;

      if (!res?.data?.conversationId && CHAT_DEV_FALLBACK_ENABLED) {
        const convRef = doc(db, 'conversations', fallbackConvId);
        await setDoc(convRef, {
          type: 'direct',
          memberIds: [u1, u2],
          createdAt: serverTimestamp(),
          lastMessageAt: null,
          lastMessageText: null,
          lastMessageSenderId: null,
        }, { merge: true });
      }

      setActiveConversationId(conversationId);
      return conversationId;
    } catch (error) {
      if (!CHAT_DEV_FALLBACK_ENABLED) {
        throw error;
      }
      try {
        const convRef = doc(db, 'conversations', fallbackConvId);
        await setDoc(convRef, {
          type: 'direct',
          memberIds: [u1, u2],
          createdAt: serverTimestamp(),
          lastMessageAt: null,
          lastMessageText: null,
          lastMessageSenderId: null,
        }, { merge: true });
        setActiveConversationId(fallbackConvId);
        return fallbackConvId;
      } catch (fallbackError) {
        console.error('[Conversation] dev fallback failed', fallbackError);
        throw error;
      }
    }
  }, [currentUser?.uid, setActiveConversationId, conversations]);

  // Create group conversation
  const createGroupConversation = useCallback(async ({ name, memberIds }) => {
    if (!currentUser?.uid) return null;
    const trimmed = (name || '').trim();
    if (!trimmed) return null;

    const members = Array.isArray(memberIds) ? memberIds.filter(id => !!id && id !== currentUser.uid) : [];
    const uniqueMembers = Array.from(new Set(members));

    if (uniqueMembers.length === 0) return null;

    const callable = httpsCallable(functions, 'createGroupConversation');
    const res = await callable({ name: trimmed, memberIds: uniqueMembers });
    const conversationId = res?.data?.conversationId || null;
    if (conversationId) {
      setActiveConversationId(conversationId);
      return conversationId;
    }
    return null;
  }, [currentUser?.uid, setActiveConversationId]);

  // Leave group conversation
  // FIX 4.3: Use ref for fresh activeConversationId to prevent stale closure
  const leaveGroupConversation = useCallback(async (conversationId) => {
    if (!currentUser?.uid || !conversationId) return;
    try {
      const callable = httpsCallable(functions, 'leaveGroupConversation');
      await callable({ conversationId });
      if (activeConvIdRef.current === conversationId) {
        setActiveConversationId(null);
      }
    } catch (error) {
      console.error('[Conversation] leaveGroupConversation error', error);
    }
  }, [currentUser?.uid, setActiveConversationId]);

  // Optimistic conversation update (used by MessageProvider for last message updates)
  const updateConversationOptimistic = useCallback((conversationId, updates) => {
    dispatch(updateConversation({ conversationId, updates }));
  }, [dispatch]);

  // FIX 8.1: Extract helper functions to useCallback for stable identity
  const getConversation = useCallback((id) => conversationMap[id], [conversationMap]);
  const isActive = useCallback((id) => activeConversationId === id, [activeConversationId]);

  const value = useMemo(() => ({
    // State
    currentUser,
    conversations,
    activeConversationId,
    conversationMap,
    membersByConversation,

    // Actions
    setActiveConversationId,
    openDirectConversationWithUser,
    createGroupConversation,
    leaveGroupConversation,
    updateConversationOptimistic,

    // Computed helpers
    getConversation,
    isActive,
  }), [
    currentUser,
    conversations,
    activeConversationId,
    conversationMap,
    membersByConversation,
    setActiveConversationId,
    openDirectConversationWithUser,
    createGroupConversation,
    leaveGroupConversation,
    updateConversationOptimistic,
    getConversation,
    isActive,
  ]);

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};

export default ConversationProvider;
